import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { SessionService } from './session.service';
import { lastValueFrom } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class AdminExportService {
  static readonly API_4D_INITIALSTATE_ADMIN_URL = 'https://lxit1gfmo0.execute-api.eu-west-2.amazonaws.com/v1';
  static readonly API_4D_MILESIGHT_ADMIN_URL = 'https://3iakg3uiv7.execute-api.eu-west-2.amazonaws.com/v1';
  static readonly API_INVIDIA_ADMIN_URL = 'https://lrbfy6e8l5.execute-api.eu-west-2.amazonaws.com/v1';

  constructor(private http: HttpClient, private sessionService: SessionService) { }

  private getUAOHeaders() {
    const token = this.sessionService.getToken();
    if (!token) {
      throw new Error('no token');
    }

    return new HttpHeaders({
      'Content-Type': 'application/json',
      'authorization': token
    });
  };

  async sendValueForGateway(gatewayId: string, payload: any, qs = {}): Promise<IBucketDetail> {
    const params = { a: 'send-value', id: gatewayId, ...qs };
    const options = { headers: this.getUAOHeaders(), params };
    const response = await lastValueFrom(this.http.post<any>(AdminExportService.API_4D_INITIALSTATE_ADMIN_URL, payload, options));

    return response;
  };

  async getBucket(gatewayId: string, qs = {}): Promise<IBucketDetail> {
    const params = { a: 'get-bucket', id: gatewayId, ...qs };
    const options = { headers: this.getUAOHeaders(), params };
    const response = await lastValueFrom(this.http.get<any>(AdminExportService.API_4D_INITIALSTATE_ADMIN_URL, options));

    return response;
  };

  async getBuckets(qs = {}): Promise<IBucket[]> {
    const params = { a: 'get-buckets', ...qs };
    const options = { headers: this.getUAOHeaders(), params };
    const response = await lastValueFrom(this.http.get<IBucket[]>(AdminExportService.API_4D_INITIALSTATE_ADMIN_URL, options));

    response.forEach(bucket => {
      if (bucket.isLogging) {
        bucket.title += ' <<extended logging>>';
      }
    });

    return response;
  };

  async getMilesightConfigs(qs = {}): Promise<IMilesightConfig[]> {
    const params = { a: 'get-configs', ...qs };
    const options = { headers: this.getUAOHeaders(), params };
    const response = await lastValueFrom(this.http.get<IMilesightConfig[]>(AdminExportService.API_4D_MILESIGHT_ADMIN_URL, options));

    response.forEach(config => {

    });

    return response;
  };

  async getMilesightDevices(qs = {}): Promise<IMilesightDevice[]> {
    const params = { a: 'get-devices', ...qs };
    const options = { headers: this.getUAOHeaders(), params };
    const response = await lastValueFrom(this.http.get<IMilesightDevice[]>(AdminExportService.API_4D_MILESIGHT_ADMIN_URL, options));

    return response;
  };

  async getMilesightPayloads(qs = {}): Promise<IMilesightPayload[]> {
    const params = { a: 'get-payloads', ...qs };
    const options = { headers: this.getUAOHeaders(), params };
    const response = await lastValueFrom(this.http.get<IMilesightPayload[]>(AdminExportService.API_4D_MILESIGHT_ADMIN_URL, options));

    return response;
  };

  async getMilesightDevice(deviceId: number, qs = {}): Promise<IMilesightDeviceDetails> {
    const params = { a: 'get-device', id: deviceId, ...qs };
    const options = { headers: this.getUAOHeaders(), params };
    const response = await lastValueFrom(this.http.get<IMilesightDeviceDetails>(AdminExportService.API_4D_MILESIGHT_ADMIN_URL, options));

    return response;
  };

  async getMilesightAction(actionId: number, qs = {}): Promise<IMilesightActionDetails> {
    const params = { a: 'get-action', id: actionId, ...qs };
    const options = { headers: this.getUAOHeaders(), params };
    const response = await lastValueFrom(this.http.get<IMilesightActionDetails>(AdminExportService.API_4D_MILESIGHT_ADMIN_URL, options));

    return response;
  };

  async getMilesightRunAction(actionId: number, qs = {}): Promise<any> {
    const params = { a: 'run', id: actionId, ...qs };
    const options = { headers: this.getUAOHeaders(), params };
    const response = await lastValueFrom(this.http.get<IMilesightActionDetails>(AdminExportService.API_4D_MILESIGHT_ADMIN_URL, options));

    return response;
  };

  async getActionsForRules(): Promise<any[]> {
    const params = { a: 'get-actions-for-rules' };
    const options = { headers: this.getUAOHeaders(), params };
    const { actions, devices, payloads } = await lastValueFrom(this.http.get<any>(AdminExportService.API_4D_MILESIGHT_ADMIN_URL, options));

    const response = actions.map(action => {
      return {
        ...action,
        device: devices.find(device => device.id === action.deviceId),
        payload: payloads.find(payload => payload.id === action.payload_id)
      };
    });



    return response;
  }

  async setMilesightActionPayload(actionId: number, payloadId: number): Promise<any> {
    const params = { a: 'set-action-payload', id: actionId, payloadid: payloadId };
    const options = { headers: this.getUAOHeaders(), params };
    const response = await lastValueFrom(this.http.get<IMilesightActionDetails>(AdminExportService.API_4D_MILESIGHT_ADMIN_URL, options));

    return response;
  };

  getMilesightCurl(actionDetails: IMilesightActionDetails): string {
    let data;
    try {
      data = JSON.stringify(JSON.parse(actionDetails.action.data));
    } catch (e) {
      console.log(e);
      data = actionDetails.action.data;
    }

    const url = `https://4dml.eu1.cloud.thethings.industries/api/v3/as/applications/${actionDetails.device.gatewayId}/devices/${actionDetails.device.deviceId}/${actionDetails.action.path}/${actionDetails.action.path}`;

    const method = actionDetails.action.actionType.toLowerCase() === 'post' ? 'POST' : 'GET';

    return `curl -H 'Authorization: Bearer ${actionDetails.config.secretKey}' -H 'Content-Type: application/json' -H 'User-Agent: ${actionDetails.config.userAgent}' -X ${method} -d '${data}' ${url}`;
  }

  async getInvidiaStatus(): Promise<any> {
    const params = { a: 'status' };
    const options = { headers: this.getUAOHeaders(), params };
    const response = await lastValueFrom(this.http.get<any>(AdminExportService.API_INVIDIA_ADMIN_URL, options));

    return response;
  }
}

export interface IMilesightConfig {
  id: number;
  title: string;
  notes: string;
  secretKey: string;
  userAgent: string;
  createdAt: Date;
}

export interface IMilesightDevice {
  id: number;
  title: string;
  notes: string;
  milesight_config_id: number;
  msTitle: string;
  deviceType: string;
  createdAt: Date;
  gatewayId: string;
  deviceId: string;
}

export interface IMilesightDeviceDetails {
  device: IMilesightDevice;
  actions: IMilesightDeviceAction[];
  payloads: IMilesightPayload[];
}

export interface IMilesightActionDetails {
  device: IMilesightDevice;
  action: IMilesightDeviceAction;
  payload: IMilesightPayload;
  config: IMilesightConfig;
  urlParts?: { url: string, host: string, path: string };
  options?: any;
  curl?: string;
  assetSettings: { id: number, title: string, setting: string, value: string, gateway_id: string }[];
}

export interface IMilesightDeviceAction {
  id: number;
  deviceId: number;
  title: string;
  actionType: 'post';
  path: string;
  data: any;
  createdAt: Date;
}

export interface IBucket {
  gatewayId: string;
  title: string;
  embedLink: string;
  bucketKey: string; // Alias
  createdAt: Date;
  isEnabled: boolean;
  updatedAt: Date;
  isLogging: boolean;
}

export interface IBucketDetail {
  bucket: IBucket;
  counts: IBucketDateCount[];
  logging: IBucketLogItem[];
}

export interface IBucketDateCount {
  ingestDate: Date;
  total: number;
}

export interface IBucketLogItem {
  ingestedAt: Date;
  hash: string;
  packet: string;
}

export interface IMilesightPayload {
  id: number;
  title: string;
  notes: string;
  createdAt: Date;
  payload: string;
  run_func?: string;
}
